import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';

const AuthCallback: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // The react-oidc-context handles the callback automatically
        // We just need to wait for the authentication to complete
        console.info('Processing OIDC callback...');
        
        // Check if there's an error in the URL
  const urlParams = new URLSearchParams(globalThis.location.search);
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        
        if (errorParam) {
          setError(`Authentication failed: ${errorParam} - ${errorDescription || 'Unknown error'}`);
          return;
        }

        // If no error and user is authenticated, redirect
        if (auth.isAuthenticated && auth.user) {
          console.info('Authentication successful, redirecting...');
          
          // Get the return URL from state or default to home
          const returnUrl = globalThis.sessionStorage.getItem('oidc_return_url') || '/';
          globalThis.sessionStorage.removeItem('oidc_return_url'); // Clean up
          
          navigate(returnUrl);
        }
      } catch {
        console.info('Error during OIDC callback handling:', error);
        setError('An error occurred during authentication. Please try again.');
      }
    };

    // Wait a moment for OIDC to process, then handle the result
    const timer = setTimeout(() => {
      if (!auth.isLoading) {
        processCallback();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [auth.isAuthenticated, auth.isLoading, auth.user, navigate]);

  // Handle authentication state changes
  useEffect(() => {
    if (auth.error) {
      setError(`Authentication error: ${auth.error.message}`);
    }
  }, [auth.error]);

  // Ensure we don't navigate during render; redirect in an effect instead
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/');
    }
  }, [auth.isAuthenticated, navigate]);

  // Show loading state
  if (auth.isLoading || (!auth.isAuthenticated && !error)) {
    return (
      <div className='loading__callback'>
        <div className='mb-1'>
          <div className='loading__spinner'></div>
        </div>
        <div>Processing authentication...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className='loading__error'>
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <button 
          onClick={() => navigate('/login')}
          className='loading__error-button'
        >
          Try Again
        </button>
      </div>
    );
  }

  if (auth.isAuthenticated) {
    return null;
  }
  return null;
};

export default AuthCallback;
